import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { EBadgePosition, EBundlesViewModel } from '@appcharge/shared-ui';
import useBadges from 'api/useBadges';
import useFeatureFlags from 'api/useFeatureFlags';
import useOffers from 'api/useOffers';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import {
  Badge,
  BundleBadge,
  EBadgeType,
  Offer,
  OfferBadge,
  OfferUI
} from 'common/contracts';
import {
  MIN_USD_PRICE,
  PRICE_VALUE_LIMIT,
  TRIMMED_INPUT_REGEX
} from 'constants/constants';
import {
  EAssetType,
  EFeatureFlag,
  EProductPriority,
  EProductType,
  EPromotionsFormState,
  ESaleDiscountDisplayType,
  ESpecialOfferInternalViewModel,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { Promotions } from 'design-system/AcSidebar/constants';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { convertProgressBarPointsToApiFormat } from 'utils/barPointsUtils';
import { timeUtils } from 'utils/scheduleUtils';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import { ProductProps } from 'views/Popups/types';
import * as yup from 'yup';

import FormHeader from 'components/FormHeader/FormHeader';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Product } from 'components/ProductsForm/ProductsForm.types';
import { generatePlayerData } from 'components/TemplatesStudio/Components/FormPage/Preview/utils/playerData';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { EToasterStatus, showToast } from '../../utils/showToast';

import PromotionPreviewSection from './components/PromotionPreviewSection';
import {
  MAP_OFFER_MODEL_TO_HAS_ONE_MAIN,
  MAP_VIEW_MODEL_TO_BADGE_TYPE,
  PROMOTIONS_TABS,
  TRANSLATE_MOCK_MAP
} from './constants';
import { usePromotionPreviewData } from './hooks/usePromotionPreviewData';
import ScheduleTab from './Tabs/ScheduleTab';
import SettingsTab from './Tabs/SettingsTab';

import '../../style/forms.scss';

const DEFAULT_OFFER_DESIGN_EXTERNAL_ID = 'Default_SpecialOffer';

interface IPromotionsForm {
  edit?: boolean;
  dup?: boolean;
}

const PromotionsForm: React.FC<IPromotionsForm> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const { specialOfferId } = useParams();
  const { canUserEdit } = usePermissions();

  const [data, setData] = useState<Partial<Offer>>({});
  const [currentOfferUI, setCurrentOfferUI] = useState('');
  const [chosenSegment, setChosenSegment] = useState<string[]>([]);
  const [tab, setTab] = useState(EPromotionsFormState.GENERAL);
  const [products, setProducts] = useState<ProductProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [active, setActive] = useState<boolean>(true);
  const [bundleModel, setBundleModel] =
    useState<ESpecialOfferInternalViewModel>();
  const [badgeList, setBadgeList] = useState<BundleBadge[]>([]);
  const [dirtyProducts, setDirtyProducts] = useState(false);
  const [startHour, setStartHour] = useState<string | undefined>('');
  const [intervalCron, setIntervalCron] = useState<string>('');
  const [showPreview, setShowPreview] = useState(true);
  const [isProgressBarFormValid, setIsProgressBarFormValid] = useState(false);
  const [dirtyProgressBar, setDirtyProgressBar] = useState(false);
  const [progressBarFormValues, setProgressBarFormValues] = useState<{
    progressBarId: string | null;
    progressBarPoints: number | null;
  }>({
    progressBarId: null,
    progressBarPoints: null
  });

  const { updateOffer, addOffer, getOffer, getOffers } = useOffers({
    offerId: specialOfferId,
    offerType: OfferType.SPECIAL_OFFER
  });
  const { getOffers: getProgressBars } = useOffers({
    offerType: OfferType.PROGRESS_BAR
  });
  const [badges, setBadges] = useState(getOffer?.data?.result?.badges);
  const [emblemBadges, setEmblemBadges] = useState<object[]>([]);
  const [ribbonBadges, setRibbonBadges] = useState<object[]>([]);
  const [progressBarOffers, setProgressBarOffers] = useState<Offer[]>([]);
  const { getOfferUI, getOffersUI } = useOffersUI(publisherId, currentOfferUI);
  const { getGeneralTheme: generalTheme, getStoreTheme } =
    useTheme(publisherId);
  const viewModel = generalTheme.data?.bundlesViewModel as EBundlesViewModel;
  const { getBadges } = useBadges(publisherId);
  const { featureFlags } = useFeatureFlags({ publisherId });

  const defaultOfferDesignId = useMemo(() => {
    if (!getOffersUI?.data?.result || getOffersUI.data.result.length === 0) {
      return '';
    }

    // Try to find the default offer design
    const defaultDesign = getOffersUI.data.result.find(
      (oi: OfferUI) => oi.externalId === DEFAULT_OFFER_DESIGN_EXTERNAL_ID
    );

    if (defaultDesign) {
      return defaultDesign._id;
    }

    // If default doesn't exist, use the first available offer design for SpecialOffer
    const firstSpecialOfferDesign = getOffersUI.data.result.find(
      (oi: OfferUI) => oi.offerUiType === OfferDesignTypes.SpecialOffer
    );

    return firstSpecialOfferDesign?._id || '';
  }, [getOffersUI?.data?.result]);

  const hasFeatureFlagTags = featureFlags?.[EFeatureFlag.DASHBOARD_TAGS];

  const hasScheduleOfferFeatureFlag =
    featureFlags?.[EFeatureFlag.DASHBOARD_SCHEDULED_OFFERS];

  const checkUniqueness = (value: string | undefined, field: keyof Offer) => {
    const isTaken = getOffers.data?.offers.find((o: Offer) => {
      if (!edit) return o[field] === value;
      return o[field] === value && data[field] !== value;
    });
    return !isTaken;
  };

  const { handleTabChange } = useTabTracking({
    sidebarTab: Promotions.title,
    tabs: PROMOTIONS_TABS,
    customNavigate: (value) => setTab(value as EPromotionsFormState)
  });

  let offerSchema = yup.object().shape({
    setAsFree: yup.boolean(),
    name: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
      .min(3, `Name length should be longer`)
      .required('Name is required')
      .test('uniqueness', 'Name already exists', (value) => {
        return checkUniqueness(value, 'name');
      }),
    description: yup
      .string()
      .nullable()
      .test(
        'is-empty-or-min-length',
        'Description length should be longer',
        (value) => !value || value.length >= 3
      ),
    offerUiId: yup.string().required('Offer Design is required'),
    publisherOfferId: yup
      .string()
      .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
      .required('External ID is required')
      .test('uniqueness', 'External ID already exists', (value) => {
        return checkUniqueness(value, 'publisherOfferId');
      }),
    price: yup.number().when('setAsFree', {
      is: true,
      then: yup
        .number()
        .oneOf([0], 'Price must be 0 if the offer is set as free'),
      otherwise: yup
        .number()
        .min(0.8, 'Price must be at least 0.8')
        .max(5000, 'Price must be at most 5000')
        .required('Price is required')
        .test('maxDigits', 'Price must have at most 10 digits', (value) =>
          value ? value.toString().length <= PRICE_VALUE_LIMIT : true
        )
    }),
    discount: yup
      .number()
      .min(0, 'Price discount cannot be negative')
      .max(99, 'Price discount cannot be more than 99%')
      .test(
        'is-one-decimal',
        'Price discount can have one decimal place at most',
        (value) =>
          value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
      ),
    priceBeforeDiscount: yup
      .number()
      .min(0, 'Price before discount cannot be negative')
      .nullable(),
    playerAvailability: yup
      .number()
      .min(1, 'Availability must be at least 1')
      .max(99, 'Availability cannot exceed 99')
      .when(['setAsFree', 'schedule.interval', 'startHour'], {
        is: (setAsFree: boolean, interval: string, startHour: string) =>
          setAsFree || interval || startHour,
        then: yup
          .number()
          .required(
            'Player availability is required when the offer is set as free, or if the offer is permanent with intervals'
          ),
        otherwise: yup.number()
      })
      .max(99, 'Availability cannot exceed 99'),
    startHour: yup
      .string()
      .test('is-empty', 'Start Hour is required', (value) => value !== '')
      .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Start Hour is required'),
    salePercentage: yup
      .number()
      .min(0, 'Sale amount cannot be negative')
      .test(
        'is-one-decimal',
        'Sale amount can have one decimal place at most',
        (value) =>
          value === undefined || /^\d+(\.\d{1})?$/.test(value.toString())
      ),
    tags: yup.array().of(yup.string())
  });

  useEffect(() => {
    currentOfferUI && getOfferUI.refetch();
  }, [currentOfferUI]);

  useEffect(() => {
    if (specialOfferId && publisherId) {
      getOffer.refetch();
    }
  }, [specialOfferId, publisherId]);

  useEffect(() => {
    if (!generalTheme.data?.specialOffersInternalViewModel) return;
    setBundleModel(generalTheme.data.specialOffersInternalViewModel);
  }, [generalTheme.data]);

  useEffect(() => {
    data?.segments && setChosenSegment(data?.segments || []);
  }, [data?.segments]);

  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, [edit, data]);

  useEffect(() => {
    if (!!specialOfferId && !getOffer.isLoading && getOffer.data) {
      setData(getOffer.data.result);
      setCurrentOfferUI(getOffer.data.result.offerUi?.offerUiId || '');
      setProducts(
        getOffer.data.result.productsSequence[0].products.map(
          (
            p: {
              product: Partial<Product>;
              quantity: number;
              quantityDisplay: string;
              priority: EProductPriority;
            },
            index: number
          ) => {
            return {
              _id: p.product?.productId || index,
              productId: p.product?.productId,
              image: p.product?.images?.[0].url,
              imagePrefix: p.product?.images?.find(
                (i) => i.type === EAssetType.PRODUCT_PREFIX
              )?.url,
              name: p.product?.name,
              amount: p?.quantity,
              type: p.product?.type,
              textFontColorHex: p.product?.textFontColorHex,
              prefix: p.product?.prefix,
              suffix: p.product?.suffix,
              publisherProductId: p.product?.publisherProductId,
              quantityDisplay: p.quantityDisplay,
              priority: p.priority || EProductPriority.SUB
            };
          }
        )
      );
      setProgressBarFormValues({
        progressBarPoints:
          getOffer.data.result.productsSequence?.[0]?.progressBarPoints?.[0]
            ?.points || null,
        progressBarId:
          getOffer.data.result.productsSequence?.[0]?.progressBarPoints?.[0]
            ?.barId || null
      });
    }
  }, [getOffer.data, getOffer.isLoading, specialOfferId]);

  const relevantBadgeType: EBadgeType | 'both' = useMemo(() => {
    if (
      bundleModel === ESpecialOfferInternalViewModel.STUDIO_DEFAULT ||
      bundleModel === ESpecialOfferInternalViewModel.DRIMAGE
    ) {
      return 'both';
    }
    if (
      bundleModel === ESpecialOfferInternalViewModel.GHOST ||
      bundleModel === ESpecialOfferInternalViewModel.MATCH_MASTERS ||
      bundleModel === ESpecialOfferInternalViewModel.GENNMU ||
      bundleModel === ESpecialOfferInternalViewModel.CASHMAN_CASINO ||
      bundleModel === ESpecialOfferInternalViewModel.MIGHTY_FU ||
      bundleModel === ESpecialOfferInternalViewModel.LOAD_COMPLETE ||
      bundleModel === ESpecialOfferInternalViewModel.ROYAL_MATCH
    )
      return EBadgeType.EMBLEM;
    if (
      bundleModel === ESpecialOfferInternalViewModel.PLAY_SIMPLE ||
      bundleModel === ESpecialOfferInternalViewModel.BANDITOS ||
      bundleModel === ESpecialOfferInternalViewModel.GHOST_DOUBLE_HIT_CASINO ||
      bundleModel === ESpecialOfferInternalViewModel.LIGHTNING_LINK_CASINO ||
      bundleModel === ESpecialOfferInternalViewModel.HEART_OF_VEGAS
    )
      return EBadgeType.RIBBON;

    if (viewModel) {
      return MAP_VIEW_MODEL_TO_BADGE_TYPE[viewModel as EBundlesViewModel];
    }

    return EBadgeType.RIBBON;
  }, [viewModel, bundleModel]);

  const filterRelevantBadges = (badgeType: EBadgeType) => {
    if (
      relevantBadgeType === 'both' ||
      ((viewModel === EBundlesViewModel.LINEAR ||
        viewModel === EBundlesViewModel.TWO_IN_ONE ||
        viewModel === EBundlesViewModel.TWO_IN_ONE_DYNAMIC ||
        viewModel === EBundlesViewModel.THREE_IN_ONE ||
        viewModel === EBundlesViewModel.SIX_IN_ONE) &&
        badgeType === relevantBadgeType)
    ) {
      return true;
    }
    return false;
  };

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  } = useFormik({
    validateOnMount: true,
    validationSchema: offerSchema,
    enableReinitialize: true,
    initialValues: {
      setAsFree:
        (edit || dup) &&
        data.productsSequence?.[0]?.priceInUsdCents !== undefined &&
        new Decimal(data.productsSequence[0].priceInUsdCents)
          .div(100)
          .toNumber() === 0,
      publisherOfferId: data.publisherOfferId || '',
      offerId: data.offerId,
      price:
        data.productsSequence?.[0]?.priceInUsdCents !== undefined
          ? new Decimal(data.productsSequence[0].priceInUsdCents)
              .div(100)
              .toNumber() === 0
            ? 0
            : new Decimal(data.productsSequence[0].priceInUsdCents)
                .div(100)
                .toNumber()
          : MIN_USD_PRICE,
      playerAvailability:
        data.productsSequence?.[0]?.playerAvailability ?? undefined,
      name: data.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      displayName: data.displayName
        ? `${data.displayName}${dup ? '_copy' : ''}`
        : data.name
          ? `${data.name}${dup ? '_copy' : ''}`
          : '',
      ...(data.description && { description: data.description }),
      type: OfferType.SPECIAL_OFFER,
      offerUiId:
        edit || dup
          ? edit
            ? data.offerUi?._id || data.offerUi?.offerUiId || ''
            : getOffer?.data?.result?.offerUi?.offerUiId || ''
          : defaultOfferDesignId,
      coolDownInHours: data.coolDownInHours || 0,
      segments: data.segments || [],
      schedule: data.schedule || {
        permanent: true,
        timeFrames: []
      },
      startHour: timeUtils.getTimeFromCron(data?.schedule?.interval || '')
        ?.hourFromCron,
      active: data.active ?? true,
      tags: data.tags || [],
      quantity: '',
      backgroundImage: data?.offerUi?.backgroundImage || '',
      productsSequence: data.productsSequence || {},
      leftBadge:
        (
          data?.badges?.find(
            (b) => b?.badge?.position === 'left' && b.badge?.type === 'emblem'
          )?.badge as Badge
        )?.badgeId || '',
      rightBadge:
        (
          data?.badges?.find(
            (b) => b?.badge?.position === 'right' && b.badge?.type === 'emblem'
          )?.badge as Badge
        )?.badgeId || '',
      badge:
        data?.badges?.find((b) => {
          return filterRelevantBadges(b.badge!.type);
        })?.badge?.badgeId || null,
      publisherBadgeId:
        data?.badges?.find((b) => {
          return filterRelevantBadges(b.badge!.type);
        })?.badge?.publisherBadgeId || '',
      salePercentage: data?.productSale?.sale || 0,
      salePercentageDisplayType:
        data.productSale?.type || ESaleDiscountDisplayType.PERCENTAGE,
      discount: data?.priceDiscount?.discount || 0,
      priceBeforeDiscount: data?.priceDiscount?.priceBeforeDiscount
        ? data.priceDiscount.priceBeforeDiscount / 100
        : null,
      priceDiscountDisplayType:
        data.priceDiscount?.type || ESaleDiscountDisplayType.PERCENTAGE,
      section: data?.section?.sectionId || data?.sectionId?.sectionId || '0',
      tab: data?.tab?.tabId || data?.tabId?.tabId || '0',
      progressBarPoints:
        data?.productsSequence?.[0]?.progressBarPoints?.[0]?.points || 0,
      progressBarId:
        data?.productsSequence?.[0]?.progressBarPoints?.[0]?.barId || ''
    },
    onSubmit: async (values) => {
      const progressBarPoints = convertProgressBarPointsToApiFormat(
        progressBarFormValues
      );
      const offerBadges: OfferBadge[] = [];
      if (values?.badge) {
        const publisherBadgeId =
          values?.publisherBadgeId || badges[0]?.badge?.publisherBadgeId;
        const badge: OfferBadge = {
          badgeId: values?.badge,
          publisherBadgeId: publisherBadgeId
        };
        offerBadges.push(badge);
      }
      const newOffer: Partial<Offer> & {
        sectionId?: string | null;
        tabId?: string | null;
      } = {
        publisherOfferId: values.publisherOfferId,
        name: values.name,
        displayName: values.displayName || values.name,
        description: values.description || null,
        type: OfferType.SPECIAL_OFFER,
        active: active,
        offerUiId: values.offerUiId,
        coolDownInHours: 1,
        segments: chosenSegment,
        schedule: {
          ...values.schedule,
          timeFrames: values?.schedule?.permanent
            ? []
            : values?.schedule.timeFrames
        },
        tags: values.tags,
        badges: offerBadges,
        productSale: {
          sale: values.salePercentage || 0,
          type:
            values.salePercentageDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        priceDiscount: {
          priceBeforeDiscount: values.priceBeforeDiscount
            ? values.priceBeforeDiscount * 100
            : null,
          discount: values.discount,
          type:
            values.priceDiscountDisplayType ||
            ESaleDiscountDisplayType.PERCENTAGE
        },
        productsSequence: [
          {
            index: 1,
            progressBarPoints,
            products: products.map((p) => ({
              productId: p.productId,
              quantity: p.amount,
              publisherProductId: p.publisherProductId,
              priority: p.priority
            })),
            priceInUsdCents: values.setAsFree
              ? 0
              : new Decimal(values.price).mul(100).toNumber(),
            ...(values.playerAvailability
              ? { playerAvailability: values.playerAvailability }
              : undefined)
          }
        ],
        sectionId:
          values.section && values.section !== '0' ? values.section : null,
        tabId: values.tab && values.tab !== '0' ? values.tab : null
      };

      if (edit && specialOfferId && !dup) {
        updateOffer.mutate(
          { offerId: specialOfferId, form: newOffer },
          {
            onSuccess: () => {
              showToast({
                message: 'Promotion edited successfully',
                status: EToasterStatus.SUCCESS
              });
              navigate('../');
            },
            onError: () => {
              showToast({
                message: 'Error editing promotion',
                status: EToasterStatus.ERROR
              });
            }
          }
        );
      } else {
        await addOffer.mutate(newOffer, {
          onSuccess: () => {
            showToast({
              message: 'New Promotion added successfully',
              status: EToasterStatus.SUCCESS
            });
            navigate('../');
          },
          onError: () => {
            showToast({
              message: 'Error adding Promotion',
              status: EToasterStatus.ERROR
            });
          }
        });
      }
    }
  });

  useEffect(() => {
    if (values.badge) {
      const currentBadge = getBadges?.data?.result?.find(
        (b: Badge) => b.badgeId === values.badge
      );
      setBadges([{ badge: currentBadge }]);
      if (currentBadge?.emblem) {
        setEmblemBadges([currentBadge.emblem]);
      }
      if (currentBadge?.ribbon) {
        setRibbonBadges([currentBadge.emblem]);
      }
    } else {
      const badges = getOffer?.data?.result?.badges;
      if (badges && badges?.length > 0) {
        setBadges(badges);
      }
    }
  }, [values?.badge]);

  useEffect(() => {
    setActive(values.active);
  }, [values?.active]);

  useEffect(() => {
    if (!getBadges.data?.result) return;
    const badgesDropdownItems = getBadges.data?.result
      .map((badge: Badge) => {
        if (filterRelevantBadges(badge.type))
          return {
            content: badge.name,
            name: badge.name,
            key: badge.badgeId,
            value: badge.badgeId,
            url: badge.emblem?.imageUrl,
            position: badge.position as EBadgePosition
          };
      })
      .filter((b: any) => b !== undefined);
    setBadgeList(badgesDropdownItems);
  }, [getBadges.data, viewModel, bundleModel]);

  useEffect(() => {
    if (!specialOfferId && defaultOfferDesignId) {
      setCurrentOfferUI(defaultOfferDesignId);

      // Also update the form value if it hasn't been set yet
      if (!values.offerUiId && setFieldValue) {
        setFieldValue('offerUiId', defaultOfferDesignId);
      }
    }
  }, [defaultOfferDesignId, specialOfferId, values.offerUiId, setFieldValue]);

  useEffect(() => {
    if (!getProgressBars.data) return;
    const progressBarOffers = getProgressBars.data.offers;
    setProgressBarOffers(progressBarOffers);
  }, [getProgressBars.data]);

  useUnsavedChanges({ dirty });
  const transformPriceValue = () => {
    if (!values.price) return MIN_USD_PRICE;
    return String(values.price).length > PRICE_VALUE_LIMIT
      ? parseFloat(String(values.price).slice(0, PRICE_VALUE_LIMIT))
      : values.price;
  };

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue,
    setTouched,
    validateField,
    validateForm
  };

  const offersViewModel: ESpecialOfferInternalViewModel = useMemo(
    () =>
      generalTheme.data?.specialOffersInternalViewModel ||
      ESpecialOfferInternalViewModel.HUGE,
    [generalTheme.data]
  );

  const productsListForPreview = useMemo(() => {
    const hasOneMain = MAP_OFFER_MODEL_TO_HAS_ONE_MAIN[offersViewModel];

    const list =
      products.length > 0
        ? products.map((p) => ({
            quantity: p?.amount,
            quantityDisplay: p?.quantityDisplay,
            images: {
              product: p?.image,
              productPrefix: p?.imagePrefix
            },
            textFontColorHex: p?.textFontColorHex,
            prefix: p?.prefix,
            suffix: p?.suffix,
            priority: p?.priority || EProductPriority.SUB,
            type: 'Quantity' as EProductType
          }))
        : [];

    if (hasOneMain && list.length > 0) {
      const isMainProductExist = list.some(
        (p) => p.priority === EProductPriority.MAIN
      );
      if (!isMainProductExist) list[0].priority = EProductPriority.MAIN;
    }
    return list;
  }, [products, offersViewModel]);

  const offerDesignItems = useMemo(
    () =>
      getOffersUI?.data?.result
        .filter(
          (oi: OfferUI) => oi.offerUiType === OfferDesignTypes.SpecialOffer
        )
        .map((offerUI: OfferUI) => {
          return {
            content: offerUI.name,
            key: offerUI._id,
            value: offerUI._id
          };
        }),
    [getOffersUI?.data?.result]
  );

  const formTitle = `${edit ? 'Edit' : 'Create'} Promotion${edit && getOffer.data?.result?.name ? ` (${getOffer.data.result.name})` : ''}`;

  const hasUnsavedChanges = !edit || dirty || dirtyProducts || dirtyProgressBar;

  const isPermanentIntervalMissingStartHour =
    values?.schedule?.permanent &&
    Boolean(values?.schedule?.interval) &&
    !values?.startHour;

  const isScheduledOfferMissingTimeFrames =
    hasScheduleOfferFeatureFlag &&
    !values?.schedule?.permanent &&
    !values?.schedule?.timeFrames?.length;

  const isFormValid =
    !isLoading &&
    isValid &&
    isProgressBarFormValid &&
    hasUnsavedChanges &&
    canUserEdit &&
    !isPermanentIntervalMissingStartHour &&
    !isScheduledOfferMissingTimeFrames;

  const bootInfo = {
    storeTheme: { ...getStoreTheme.data, storeScreen: {} },
    featureFlags: featureFlags || {}
  };

  const dynamicOfferData = usePromotionPreviewData({
    values,
    getOfferUI,
    productsListForPreview,
    badges,
    emblemBadges,
    ribbonBadges,
    transformPriceValue
  });

  const previewProps = {
    translate: (key: string) => TRANSLATE_MOCK_MAP[key] || key,
    playerData: generatePlayerData(),
    boot: bootInfo,
    offers: dynamicOfferData
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        closeFormOnSubmit={false}
        onCloseForm={() => navigate('../')}
        onMainButtonClick={() => submitForm()}
        title={formTitle}
        isFormValid={isFormValid}
        mainButtonText="Save"
        secondaryButtonText={showPreview ? 'Hide Preview' : 'Show Preview'}
        onSecondaryButtonClick={() => setShowPreview(!showPreview)}
      />
      <div className="flex h-[calc(100vh-73px)]">
        <div
          className={`flex justify-center overflow-y-auto px-3 ${showPreview ? 'w-1/2' : 'w-full'}`}
        >
          <Tabs
            className="h-full pt-10 pb-10 w-[528px]"
            value={tab}
            onValueChange={handleTabChange}
          >
            <FormSectionsTabs
              currentTab={tab}
              tabs={[
                {
                  value: EPromotionsFormState.GENERAL,
                  label: 'Settings',
                  disabled: getOffer.isLoading && edit
                },
                ...(hasScheduleOfferFeatureFlag
                  ? [
                      {
                        value: EPromotionsFormState.SCHEDULE,
                        label: 'Schedule',
                        disabled: getOffer.isLoading && edit
                      }
                    ]
                  : [])
              ]}
            />
            <div className="flex-1 flex justify-center">
              <TabsContent
                value={EPromotionsFormState.GENERAL}
                className="mt-9 w-full"
              >
                <SettingsTab
                  formikProps={formikProps}
                  values={values}
                  setCurrentOfferUI={setCurrentOfferUI}
                  chosenSegment={chosenSegment}
                  setChosenSegment={setChosenSegment}
                  hasFeatureFlagTags={hasFeatureFlagTags}
                  specialOfferId={specialOfferId}
                  products={products}
                  setProducts={setProducts}
                  edit={edit}
                  relevantBadgeType={relevantBadgeType}
                  badgeList={badgeList}
                  setDirtyProducts={setDirtyProducts}
                  setStartHour={setStartHour}
                  offerDesignItems={offerDesignItems}
                  dup={dup}
                  progressBarOffers={progressBarOffers}
                  getProgressBars={getProgressBars}
                  setIsProgressBarFormValid={setIsProgressBarFormValid}
                  setDirtyProgressBar={setDirtyProgressBar}
                  setProgressBarFormValues={setProgressBarFormValues}
                  progressBarFormValues={progressBarFormValues}
                />
              </TabsContent>
              {hasScheduleOfferFeatureFlag && (
                <TabsContent
                  value={EPromotionsFormState.SCHEDULE}
                  className={`mt-9 w-full`}
                >
                  <ScheduleTab
                    formikProps={formikProps}
                    values={values}
                    updateOffer={updateOffer}
                    edit={edit}
                    startHour={startHour}
                    setStartHour={setStartHour}
                    intervalCron={intervalCron}
                    setIntervalCron={setIntervalCron}
                  />
                </TabsContent>
              )}
            </div>
          </Tabs>
        </div>
        {showPreview && (
          <PromotionPreviewSection
            previewProps={previewProps}
            storeThemeData={getStoreTheme.data}
          />
        )}
      </div>
    </motion.div>
  );
};

export default PromotionsForm;
